import { EventEmitter } from "events";
import { sleep } from "../../utils/reflection";
import { UIService } from "../UIService";
import { BaseActor } from "../../components/threeDComponents/behaviors/BaseActor";
import { v4 } from "uuid";
import { AiActor, AiObject } from "./ui-signaling-dex";
import { generatePngMap } from "../../utils/spatial";
import { arrayBufferToBase64 } from "../../utils/ioUtils";
import { SignalingConnection } from "../signaling/SignalingConnection";
import { showSnackbar } from "../../components/UniversalDialog";
import { loadOrgRecords, loadRecords } from "../connection";
import { Robot } from "../../models/Robot";

export class SimClient extends EventEmitter {
  public adminConnection!: SignalingConnection;
  public simConnection!: SignalingConnection;
  public id = '';
  public runtimeId = '';
  public buildStatus = '';
  public actors = new Map<string, BaseActor>();
  public poseInterval: NodeJS.Timeout | undefined;

  constructor(public uiService: UIService) {
    super();
    this.id = v4();
    this.adminConnection = new SignalingConnection({
      deviceId: uiService.org._id,
      group: 'techmage-simulation-admin',
      token: uiService.org.token,
      receiver: this
    });
    this.simConnection = new SignalingConnection({
      deviceId: uiService.org._id,
      group: 'techmage-simulation',
      token: uiService.org.token,
      receiver: this
    });
  }

  public async onRuntimeAvailable({ id, mapId }: { id: string, mapId: string }) {
    console.log({ id, mapId });
    if (this.uiService.map._id === mapId) {
      this.updateState({ runtimeId: id });
    }
  }

  public async onProps(props: any) {
    for (const prop of props) {
      const actor = this.actors.get(prop.actorId);
      if (actor) {
        actor.updateState({ properties: prop });
      }
    }
  }

  public async onTwists(twists: any[]) {
    twists.forEach((twist) => {
      const actor = this.actors.get(twist.actorId);
      if (actor && !actor.isEnabled('Connect')) {
        actor.updateState({ twist });
        if (twist.state) {
          actor.enableActorState(twist.state);
        }
      }
    });
  }
  private initialized = false;
  public async initializeAdmin() {
    if (this.initialized) {
      return;
    }
    // console.log({ connectingWithToken: this.adminConnection.config.token });
    await this.adminConnection.connect();
    // this.adminConnection.sendSpawn('mapSim');
    // await sleep(1000);
    this.initialized = true;
  }

  public async start() {
    await this.initializeAdmin();
    await sleep(1000);
    this.adminConnection.sendReplace({ start: { runtimeId: this.runtimeId } });
    this.simConnection.config.group = 'techmage-simulation';
    this.simConnection.config.deviceId = `${this.uiService.org._id}-${this.runtimeId}`;
    this.simConnection.config.token = this.uiService.org.token;
    this.simConnection.config.slot = '2';
    await this.simConnection.connect();
  }

  public async onSimReady(ready: boolean) {
    const objects = this.getObjects();
    this.simConnection.sendReplace({ objects });
    const actors = this.uiService.scene.actors.filter((actor) => Boolean(actor.id));
    const robots = await loadOrgRecords<Robot>('robots');
    const robotIdToNameMap = new Map<string, string>();
    robots.forEach((robot) => {
      robotIdToNameMap.set(robot._id, robot.name);
    });
    this.actors = new Map<string, BaseActor>();
    actors.forEach((actor) => {
      // actor.id = 'Badguy123';
      const actorId = robotIdToNameMap.get(actor.id) || actor.id;
      this.actors.set(actorId, actor);
    });
    this.poseInterval = setInterval(() => {
      const poses: any[] = [];
      for (const [actorId, actor] of this.actors) {
        const [x, y, z, w] = actor.pose;
        // const rotationZ = Math.sin(w / 2);
        // const roataionW = Math.cos(w / 2);
        const actorData: any = {
          actorId: actorId,
          position: { x, y, z },
          rotation: { x: 0, y: 0, z: w },
          maxSpeed: actor.maxSpeed
        };
        poses.push(actorData);
      }
      // console.log('sending poses', poses);
      this.simConnection.sendReplace({ poses });
    }, 200);
    // setTimeout(() => {
    this.simConnection.sendReplace({ start: this.getObjects() });
    // }, 1500);
  }

  public async build() {
    await this.initializeAdmin();
    this.updateState({ runtimeId: '', buildStatus: 'Starting Build' });
    const actors: AiActor[] = [];
    const robots = await loadOrgRecords<Robot>('robots');
    const robotIdToNameMap = new Map<string, string>();
    robots.forEach((robot) => {
      robotIdToNameMap.set(robot._id, robot.name);
    });
    for (const actor of this.uiService.scene.actors) {
      const [x, y, z, w] = actor.pose;
      actors.push({
        id: robotIdToNameMap.get(actor.id) || actor.id,
        position: { x, y, z },
        rotation: { x: 0, y: 0, z: w },
        states: actor.states.filter(s => s.name !== 'Show Camera').map((state) => state.name),
        script: actor.script,
        actorType: actor.constructor.name
      });
    }
    const objects: AiObject[] = this.getObjects();
    const pngMap = await generatePngMap(this.uiService, 200, 0.5);
    console.log({ objects });
    const base64String = arrayBufferToBase64(pngMap);

    this.adminConnection.sendReplace({
      build: {
        id: this.uiService.map._id,
        map: this.uiService.map,
        pngMap: base64String,
        actors,
        objects
      }
    });
  }

  public async buildScenario(scenarioId: string) {
    await this.initializeAdmin();
    this.updateState({ runtimeId: '', buildStatus: 'Starting Scenario Build' });
    const scenario = this.uiService.map.scenarios.find(s => s.id === scenarioId);
    if (!scenario) {
      showSnackbar(`Scenario ${scenarioId} not found`, 'error');
      return; 
    }
  }

  private getObjects() {
    const objects: AiObject[] = [];
    const map = this.uiService.map;
    for (const region of map.regions) {
      const aiObject: AiObject = {
        id: region.label,
        position: { x: region.pose.x, y: region.pose.y, z: region.elevation || 0 },
        corners: [],
        objectType: 'Region'
      };
      aiObject.corners = [
        { x: region.pose.x - region.size.x / 2, y: region.pose.y - region.size.y / 2, z: region.elevation || 0 },
        { x: region.pose.x + region.size.x / 2, y: region.pose.y - region.size.y / 2, z: region.elevation || 0 },
        { x: region.pose.x + region.size.x / 2, y: region.pose.y + region.size.y / 2, z: region.elevation || 0 },
        { x: region.pose.x - region.size.x / 2, y: region.pose.y + region.size.y / 2, z: region.elevation || 0 }
      ];
      objects.push(aiObject);
    }
    return objects;
  }

  public async stop() {
    if (this.poseInterval) {
      clearInterval(this.poseInterval);
    }
    if (this.simConnection) {
      this.simConnection.sendReplace({ stop: true });
      this.simConnection.close();
    }

    const as = Array.from(this.actors.values());
    as.forEach((actor) => {
      const twist = { linear: { x: 0, y: 0, z: 0 }, angular: { x: 0, y: 0, z: 0 } };
      actor.updateState({ twist });
    });
  }

  public onBuildStatus(buildStatus: string) {
    this.updateState({ buildStatus });
    showSnackbar(buildStatus, 'success');
  }

  public updateState(dgram: Partial<SimClient>) {
    Object.assign(this, dgram);
    this.emit('stateChange', dgram);
  }
}
